import { useState, useMemo } from "react";
import React from "react";
import { SelectionBoxWrapper } from "./SelectionBoxWrapper";
import { DisplayChart } from "../../containers/DisplayChart";
import { TSensorTypes } from "../../types/generated_types";
import { useTranslation} from "react-i18next";
import { ChartTypes, ReportConfigParams } from "../../types/types";
import { InfoBlock } from "./InfoBlock";

interface Props {
  reportConfig: ReportConfigParams;
  onNodesChange: (updatedNodes: ReportConfigParams) => void;
}

function ChartTabContent({
  reportConfig,
  onNodesChange,
}: Props) {
  const [chartLoading, setChartLoading] = useState<boolean>(true);

  const { t } = useTranslation();

  const nodes = reportConfig.nodes;

  const handleNodeToggle = (nodeId: number) => {
    const updatedNodes = nodes.map(node => {
      if (node.id === nodeId) {
        const isBeingSelected = !node.selected;

        // Get all enabled sensors from selected nodes
        const enabledSensors = new Set(
          nodes
            .filter(n => n.selected)
            .flatMap(n => n.fields.filter(s => s.enabled).map(s => s.sens_type))
        );

        return {
          ...node,
          selected: isBeingSelected,
          fields: node.fields.map(sensor => ({
            ...sensor,
            enabled: isBeingSelected
              ? enabledSensors.has(sensor.sens_type) || sensor.enabled
              : false,
          })),
        };
      }
      return node;
    });
    onNodesChange({ ...reportConfig, nodes: updatedNodes });
  };

  const handleSensorToggle = (sensorType: string) => {
    const updatedNodes = nodes.map(node => {
      if (node.selected) {
        // Check if the sensor is enabled on any selected node
        const isEnabledOnAnyNode = nodes
          .filter(n => n.selected)
          .some(n => n.fields.some(s => s.sens_type === sensorType && s.enabled));

        // Toggle the sensor:
        // disable it on all nodes if it was enabled
        // else enable it on all nodes
        return {
          ...node,
          fields: node.fields.map(sensor =>
            sensor.sens_type === sensorType
              ? { ...sensor, enabled: !isEnabledOnAnyNode }
              : sensor
          ),
        };
      }
      return node;
    });
    onNodesChange({...reportConfig, nodes:updatedNodes});
  };

  // Used for selecting the secondary valueAxis sensor type
  // and displaying types next to the chart
  const selSensorTypes = useMemo(() => {
    if (!nodes) return [];

    const tempTypes = new Set<TSensorTypes>();
    nodes.forEach((node) => {
      node.fields.forEach((sens) => {
        if (sens.enabled) {
          tempTypes.add(sens.sens_type);
        }
      });
    });

    return Array.from(tempTypes);
  }, [nodes]);

  const noNodesSelected = useMemo(() => {
    return !nodes.some(node => node.selected);
  }, [nodes]);

  const noSensorsSelected = useMemo(() => {
    return !nodes
      .filter(node => node.selected)
      .some(node => node.fields.some(field => field.enabled));
  }, [nodes]);

  return (
    <>
      {noNodesSelected || noSensorsSelected ? (
        <InfoBlock
          header={t("No data selected")}
          messages={[
            noNodesSelected
              ? t("Please select a sensor from the selection panel")
              : t("Please select at least one data type for the selected sensor(s)")
          ]}
        />
      ) : (
        <DisplayChart
          selSensorTypes={selSensorTypes}
          secondaryAxis={undefined}
          forcedTimezone={reportConfig.timezone}
          nodes={nodes}
          noNodesAvailable={nodes.length === 0}
          startDate={reportConfig.x1}
          endDate={reportConfig.x2}
          chartType={ChartTypes.Normal}
          loading={chartLoading}
          setLoading={setChartLoading}
          isThisReport
        />
      )}
        <SelectionBoxWrapper
          nodes={nodes}
          handleNodeToggle={handleNodeToggle}
          handleSensorToggle={handleSensorToggle}
        />
    </>
  );
}

export default React.memo(ChartTabContent);